<script setup lang="ts">
    import PackageItem from '@/components/packages/PackageItem.ce.vue';
    import { type PackageTyping } from '@/typings/widget';
    import { useI18n } from 'vue-i18n';

    const { t } = useI18n();

    defineProps<{
        packages: PackageTyping[] | null,
        inPath?: boolean
    }>();

    const ZELF_IMG = `${import.meta.env.VITE_CDN_URL}/static/ill-zelf.svg`;

    const emit = defineEmits<{(event: 'packageSelected', value: PackageTyping), (event: 'createOwn')}>();

    const { venue } = storeToRefs(useWidgetVenueStore());
</script>

<template>
    <div class="package-gallery">
        <div v-if="packages">
            <ul class="package__list__declaration">
                <li
                    v-if="inPath && !venue?.allStarsQuoteEnabled"
                    class="package__list"
                >
                    <button
                        type="button"
                        class="package-business-flow"
                        @click="emit('createOwn')"
                    >
                        <img :src="ZELF_IMG">
                        <h2>{{ t('widget.package-list.self-title') }}</h2>
                        <span>{{ t('widget.package-list.self-content') }}</span>
                    </button>
                </li>
                <li
                    v-for="pkg in packages"
                    :key="pkg.id"
                    class="package__list"
                >
                    <PackageItem
                        :package="pkg"
                        @click="emit('packageSelected', pkg)"
                    />
                </li>
            </ul>
        </div>
        <div
            v-if="packages && packages.length === 0"
            class="no-package-results"
        >
            {{ t('widget.package-list.no-results') }}
        </div>
    </div>
</template>

<style scoped lang="scss">

.package-gallery {
    .package__list__declaration {
        display: grid;
        grid-gap: var(--widget-base-gutter);
        grid-template-columns: repeat(auto-fill, minmax(270px, 1fr));
        justify-items: center;
        list-style-type: none;
        padding-block: var(--spacing-04);
        padding-inline: var(--spacing-03);

        @include min-width(md) {
            padding-block: var(--spacing-05);
            padding-inline: var(--spacing-05);
        }

        @include min-width(lg) {
            padding-inline: var(--spacing-07);
        }
    }

    .package__list {
        position: relative;
        border-radius: var(--border-radius-sm);
        background-color: var(--background-secondary);
        inline-size: 100%;
    }

    .package-business-flow {
        position: relative;
        display: flex;
        overflow: hidden;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: var(--spacing-05);
        border: 1px solid var(--border-tertiary);
        border-radius: var(--border-radius-sm);
        background: rgb(255 255 255 / 0.4);
        block-size: 100%;
        cursor: pointer;
        inline-size: 100%;
        min-block-size: 345px;
        transition: all 0.2s ease;

        img {
            margin-block-end: var(--spacing-03);
            max-block-size: 135px;
        }

        &:hover {
            background: rgb(255 255 255 / 0.8);
            transform: translateY(-2px);
        }
    }
}

.no-package-results {
    padding: var(--widget-inner-gutter);
    font-size: var(--font-regular);
    text-align: center;
}

</style>
